import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { shareGame } from "@/lib/share";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import WatchBoard from "@/components/WatchBoard";
import { tilesText } from "@/components/TileArt";
import { buildSteps, fromServerMoves } from "@/components/ReviewModal";
import { boardShape, LAYOUT_NAMES } from "@/lib/boardShapes";
import { ago, resultText, TURN_LIMIT } from "@/lib/watch";
import { ArrowLeft, CaretDoubleLeft, CaretDoubleRight, CaretLeft, CaretRight, House, LockSimple, ShareNetwork, Trophy } from "@phosphor-icons/react";

const POLL_MS = 10000;

function PlayerBar({ id, name, elo, side, badge }) {
  const ring = side === 0 ? "border-brand text-brand bg-brand/15" : "border-opp text-opp bg-opp/15";
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 px-1">
      <div className="flex items-center gap-2.5 min-w-0">
        <Avatar userId={id} username={name} className={`w-9 h-9 text-sm border-[1.5px] ${ring}`} fallbackClassName="" />
        <PlayerLink id={id} className="font-display font-bold truncate">{name}</PlayerLink>
        {elo != null && <span className="text-xs text-muted-foreground font-semibold whitespace-nowrap">{elo} Elo</span>}
      </div>
      {badge}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-bold text-right">{children}</span>
    </div>
  );
}

export default function WatchGame() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  // Opened from a profile, the lobby or Game stats (state.back): Back returns
  // there instead of to the Watch list (Jack, 2026-10-29).
  const backToPrev = !!useLocation().state?.back;
  const [game, setGame] = useState(null);
  const [missing, setMissing] = useState(false);
  const [idx, setIdx] = useState(null); // null: follow the latest turn
  const { user, loading: authLoading } = useAuth();
  // Signed out: a shared link, which opens finished games only.
  const path = user ? `/watch/${matchId}` : `/share/game/${matchId}`;

  const load = useCallback(() => (authLoading ? Promise.resolve() : api.get(path))
    .then(({ data }) => setGame(data))
    .catch((e) => { if (e?.response?.status === 404) setMissing(true); }), [path, authLoading]);

  useEffect(() => { setGame(null); setIdx(null); setMissing(false); load(); }, [load]);
  // Live games refresh while they're open.
  const live = game?.status === "active";
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [live, load]);

  const shape = boardShape(game?.layout);
  const steps = useMemo(
    () => (game ? buildSteps(fromServerMoves(game.moves, game.player1_id), shape.rows) : []),
    [game, shape.rows],
  );
  const last = Math.max(0, steps.length - 1);
  const at = idx == null ? last : Math.min(idx, last);
  const step = steps[at];
  // Each step's turn number: the mover's own count of turns.
  const turnNo = useMemo(() => {
    let a = 0, b = 0;
    return steps.map((s) => (s.kind === "start" ? 0 : s.actor === "me" ? ++a : ++b));
  }, [steps]);

  const go = useCallback((d) => setIdx((i) => {
    const n = Math.max(0, Math.min(last, (i == null ? last : i) + d));
    return n === last ? null : n;
  }), [last]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "Home") setIdx(last === 0 ? null : 0);
      if (e.key === "End") setIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, last]);

  const tiles = useMemo(() => {
    const out = {};
    Object.entries(step?.tiles || {}).forEach(([k, t]) => { out[k] = { side: t.color === "me" ? 0 : 1, letter: t.letter }; });
    return out;
  }, [step]);
  const added = useMemo(() => new Set(step?.added || []), [step]);

  const stats = useMemo(() => {
    const count = [0, 0];
    let best = [null, null];
    Object.entries(tiles).forEach(([k, t]) => {
      count[t.side] += 1;
      const r = Number(k.split(",")[0]);
      // Rows left to the far edge of the other side's base.
      const gap = t.side === 0 ? r - Math.max(...shape.base.A.map(([br]) => br)) - 1
        : Math.min(...shape.base.H.map(([br]) => br)) - r - 1;
      if (best[t.side] == null || gap < best[t.side]) best[t.side] = gap;
    });
    return { count, best };
  }, [tiles, shape]);

  if (missing) {
    return (
      <main className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="watch-missing">
        <h1 className="font-display text-3xl font-black">Game not found</h1>
        <p className="text-muted-foreground mt-2">{user ? "It may still be in progress against a bot, which isn't shown here." : "Shared games can be watched once they're finished."}</p>
        <button type="button" onClick={() => navigate(user ? "/watch" : "/")} className="mt-6 underline">{user ? "Back to all games" : "Play Jangle"}</button>
      </main>
    );
  }
  if (!game) {
    return <main className="max-w-7xl mx-auto px-6 py-14 text-muted-foreground">Loading the game…</main>;
  }

  const p1 = { id: game.player1_id, name: game.player1_username, elo: game.player1_elo };
  const p2 = { id: game.player2_id, name: game.player2_username, elo: game.player2_elo };
  const moverPill = (p) => live && game.turn_user_id === p.id && (
    <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-400/15 text-emerald-500 dark:text-emerald-300 flex items-center gap-1.5 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Their move
    </span>
  );
  const winnerPill = (p) => !live && game.winner_id === p.id && (
    <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-brand/15 text-brand flex items-center gap-1.5">
      <Trophy size={12} weight="fill" /> Winner
    </span>
  );
  const share = () => shareGame({
    matchId: game.match_id,
    title: `${p2.name} vs ${p1.name} · Jangle`,
    text: `${resultText(game)}. Step through every turn:`,
  });
  const words = step ? tilesText(step.added.map((k) => step.tiles[k]?.letter)) : "";
  const moverName = step?.actor === "me" ? p1.name : step?.actor === "opp" ? p2.name : null;
  const turnLabel = at === 0 ? "Start" : `Turn ${Math.min(turnNo[at], TURN_LIMIT)} of ${TURN_LIMIT}`;
  const closest = [0, 1].filter((s) => stats.best[s] != null).sort((a, b) => stats.best[a] - stats.best[b])[0];

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-3 md:py-6" data-testid="watch-game">
      <div className="flex items-center justify-between gap-2 mb-2 md:mb-4">
        {user ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => (backToPrev ? navigate(-1) : navigate("/watch"))}
              className="inline-flex items-center gap-2 font-display font-extrabold text-sm border border-border rounded-xl px-4 py-2" data-testid="watch-back">
              <ArrowLeft size={14} weight="bold" /> {backToPrev ? "Back" : "All games"}
            </button>
            <button type="button" onClick={() => navigate("/lobby")}
              className="inline-flex items-center gap-2 font-display font-extrabold text-sm border border-border rounded-xl px-4 py-2" data-testid="watch-lobby">
              <House size={15} weight="bold" /> Lobby
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-display font-extrabold text-sm rounded-xl px-4 py-2 bg-brand text-brand-foreground" data-testid="watch-play">
            Play Jangle
          </button>
        )}
        {!live && (
          <button type="button" onClick={share}
            className="inline-flex items-center gap-2 font-display font-extrabold text-sm border border-border rounded-xl px-4 py-2" data-testid="watch-share">
            <ShareNetwork size={15} weight="bold" /> Share
          </button>
        )}
      </div>
      <div className="flex flex-col md:flex-row md:justify-center gap-5 md:gap-9">
      <div className="w-full md:w-auto">
        <div className="mx-auto" style={{ width: `min(100%, calc((100vh - 17rem) * ${shape.cols} / ${shape.rows}))`, minWidth: 260 }}>
          <PlayerBar {...p2} side={1} badge={moverPill(p2) || winnerPill(p2)} />
          <WatchBoard
            shape={shape}
            tiles={tiles}
            added={added}
            finished={!live}
            className={idx != null ? "ring-[3px] ring-[#f5b942]" : ""}
          />
          <PlayerBar {...p1} side={0} badge={moverPill(p1) || winnerPill(p1)} />
          <div className="flex items-center justify-between gap-3 mt-2">
            <div className="flex gap-2">
              <button type="button" aria-label="Start of the game" onClick={() => setIdx(last === 0 ? null : 0)} disabled={at === 0}
                className="w-10 h-10 rounded-xl border border-border bg-surface grid place-items-center disabled:opacity-40" data-testid="watch-start">
                <CaretDoubleLeft size={18} weight="bold" />
              </button>
              <button type="button" aria-label="Previous turn" onClick={() => go(-1)} disabled={at === 0}
                className="w-10 h-10 rounded-xl border border-border bg-surface grid place-items-center disabled:opacity-40" data-testid="watch-prev">
                <CaretLeft size={18} weight="bold" />
              </button>
              <button type="button" aria-label="Next turn" onClick={() => go(1)} disabled={at === last}
                className="w-10 h-10 rounded-xl border border-border bg-surface grid place-items-center disabled:opacity-40" data-testid="watch-next">
                <CaretRight size={18} weight="bold" />
              </button>
              <button type="button" aria-label="Latest turn" onClick={() => setIdx(null)} disabled={at === last}
                className="w-10 h-10 rounded-xl border border-border bg-surface grid place-items-center disabled:opacity-40" data-testid="watch-end">
                <CaretDoubleRight size={18} weight="bold" />
              </button>
            </div>
            <span className="font-display font-bold whitespace-nowrap" data-testid="watch-turn">{turnLabel}</span>
          </div>
          {moverName && (
            <p className="text-xs text-muted-foreground mt-2 text-right" data-testid="watch-caption">
              {step.kind === "final" ? `${moverName}'s last move` : `${moverName} played`}
              {words && <span className="font-mono tracking-wider text-foreground/80"> +{words}</span>}
            </p>
          )}
        </div>
      </div>

      <aside className="bg-surface border border-border rounded-2xl p-5 md:p-6 md:w-80 md:self-start">
        <div className="flex items-center justify-between gap-2">
          {live ? (
            <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-400/15 text-emerald-500 dark:text-emerald-300 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live
            </span>
          ) : (
            <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-muted text-muted-foreground">Finished</span>
          )}
          <span className="text-xs text-muted-foreground">
            {LAYOUT_NAMES[game.layout] || "Jangle"}{game.mode === "turn_based" ? " · Turn based" : ""}
          </span>
        </div>
        <h1 className="font-display text-2xl font-black mt-3.5">{p2.name} vs {p1.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {live
            ? `Started ${ago(game.created_at) || "a while ago"}. The board updates when a turn is played.`
            : `${resultText(game)} · ${ago(game.completed_at)}`}
        </p>
        <div className="mt-5 flex flex-col gap-2.5 text-sm">
          <Row label="Tiles on board">{stats.count[1]} · {stats.count[0]}</Row>
          {closest != null && (
            <Row label="Closest to base">
              <span className={closest === 0 ? "text-brand" : "text-opp"}>
                {closest === 0 ? p1.name : p2.name}, {Math.max(0, stats.best[closest])} {stats.best[closest] === 1 ? "row" : "rows"}
              </span>
            </Row>
          )}
          {words && <Row label="Last word">{words}</Row>}
        </div>
        <p className="text-xs text-muted-foreground mt-5 flex gap-2 items-start">
          <LockSimple size={14} className="flex-none mt-px" />
          Hands and the bag are never shown. Only played turns appear.
        </p>
      </aside>
      </div>
    </main>
  );
}
